import { createFileRoute, Link } from '@tanstack/react-router';
import { motion } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from '@/components/ui/empty';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { bounce, transitions } from '@/lib/motion-config';

export const Route = createFileRoute('/$')({
  component: NotFoundPage,
});

export default function NotFoundPage() {
  const { t } = useTranslation('common');
  const documentTitle = `${t('notFound.title', '404 - Page Not Found')} · ${t('app.name', 'One Score')}`;
  useDocumentTitle(documentTitle);

  return (
    <div className="flex min-h-[calc(100vh-8rem)] items-center justify-center p-4">
      <motion.div
        variants={bounce}
        initial="initial"
        animate="animate"
        transition={transitions.springBouncy}
        className="max-w-md"
      >
        <Empty>
          <EmptyHeader>
            <motion.div
              animate={{
                scale: [1, 1.1, 1],
                rotate: [0, 5, -5, 0],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                repeatDelay: 3,
                ease: 'easeInOut',
              }}
            >
              <EmptyTitle className="text-4xl font-bold">404</EmptyTitle>
            </motion.div>
            <EmptyTitle>{t('notFound.title', 'One Path Does Not Lead Here')}</EmptyTitle>
            <EmptyDescription className="text-base">
              {t(
                'notFound.description',
                'This path has been lost to the ages. Even the One Score cannot find what you seek. Return to the realm of sessions and continue your quest.',
              )}
            </EmptyDescription>
          </EmptyHeader>
          <EmptyContent>
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button asChild size="lg">
                <Link to="/">{t('notFound.backHome', 'Return to Home')}</Link>
              </Button>
            </motion.div>
          </EmptyContent>
        </Empty>
      </motion.div>
    </div>
  );
}
